// ─────────────────────────────────────────────
// KLOTHIQ — Cashbook calculation helpers (FEAT-ACCOUNTS-001)
// Pure functions only — no React, no Supabase — so they are unit-testable.
// Money maths (running balance, totals) is done server-side in get_cashbook;
// these helpers only classify, group and format.
// ─────────────────────────────────────────────

export type CashbookDirection = 'income' | 'expense' | 'neutral' | 'transfer' | 'opening';

export type CashbookTypeKey =
  | 'sale'
  | 'purchase'
  | 'capital'
  | 'expense'
  | 'refund'
  | 'adjustment'
  | 'opening'
  | 'transfer';

export const DEFAULT_TYPE_KEYS: CashbookTypeKey[] = [
  'sale', 'purchase', 'capital', 'expense', 'refund', 'adjustment', 'opening',
];

export const ALL_TYPE_KEYS: CashbookTypeKey[] = [...DEFAULT_TYPE_KEYS, 'transfer'];

export const TYPE_KEY_LABELS: Record<CashbookTypeKey, string> = {
  sale: 'Sales',
  purchase: 'Purchases',
  capital: 'Capital',
  expense: 'Expenses',
  refund: 'Refunds',
  adjustment: 'Adjustments',
  opening: 'Opening',
  transfer: 'Transfers',
};

export interface CashbookRow {
  id: string;
  transaction_date: string;
  created_at: string;
  particular: string;
  notes: string | null;
  transaction_type: string;
  direction: CashbookDirection;
  amount: number;
  income_amount: number;
  expense_amount: number;
  running_balance: number;
  account_id: string;
  account_name: string;
  account_is_active: boolean;
  partner_id: string | null;
  partner_name: string | null;
  expense_category_id: string | null;
  category_name: string | null;
  reference_type: string | null;
  reference_id: string | null;
  payment_method: string | null;
  sale_number: string | null;
  customer_name: string | null;
  purchase_number: string | null;
  supplier_name: string | null;
  import_batch_id: string | null;
  voidable: boolean;
}

export interface CashbookTotals {
  income: number;
  expense: number;
  neutral: number;
  closing_balance: number;
}

export interface CashbookResponse {
  opening_balance: number;
  is_filtered: boolean;
  totals: CashbookTotals;
  rows: CashbookRow[];
  page: number;
  page_size: number;
  total_rows: number;
}

/** BR-001 — mirrors the SQL CASE in v_cashbook_entries. */
export function classifyType(transactionType: string): CashbookDirection {
  switch (transactionType) {
    case 'sale_payment':
    case 'capital_contribution':
      return 'income';
    case 'purchase_payment':
    case 'expense':
    case 'refund':
      return 'expense';
    case 'transfer_in':
    case 'transfer_out':
      return 'transfer';
    case 'opening_balance':
      return 'opening';
    default:
      return 'neutral';
  }
}

export function typeKeyOf(transactionType: string): CashbookTypeKey {
  switch (transactionType) {
    case 'sale_payment': return 'sale';
    case 'purchase_payment': return 'purchase';
    case 'capital_contribution': return 'capital';
    case 'expense': return 'expense';
    case 'refund': return 'refund';
    case 'opening_balance': return 'opening';
    case 'transfer_in':
    case 'transfer_out': return 'transfer';
    default: return 'adjustment';
  }
}

/** Human label for the Type/Source column. */
export function sourceLabel(row: Pick<CashbookRow, 'transaction_type' | 'reference_type' | 'category_name' | 'partner_name'>): string {
  const imported = row.reference_type === 'import' ? ' · imported' : '';
  switch (row.transaction_type) {
    case 'sale_payment': return `Sale${imported}`;
    case 'purchase_payment': return `Purchase${imported}`;
    case 'capital_contribution': return `Capital${row.partner_name ? ` · ${row.partner_name}` : ''}`;
    case 'expense': return `${row.category_name ?? 'Uncategorized'}${imported}`;
    case 'refund': return 'Refund';
    case 'transfer_in': return 'Transfer in';
    case 'transfer_out': return 'Transfer out';
    case 'opening_balance': return 'Opening balance';
    default: return 'Adjustment';
  }
}

// ── FR-005 — group sales by day ───────────────────────────────

export type DisplayRow =
  | { kind: 'single'; key: string; row: CashbookRow }
  | {
      kind: 'sale_group';
      key: string;
      date: string;
      rows: CashbookRow[];
      income: number;
      running_balance: number;
    };

/**
 * Collapses all sale_payment rows sharing a date into one "SALES (n orders)" row.
 * The group is placed where the LAST sale of that date appears, and carries that
 * row's running balance, so every balance shown stays correct. A date with a
 * single sale is left as a plain row.
 */
export function groupSalesByDay(rows: CashbookRow[]): DisplayRow[] {
  const salesByDate = new Map<string, CashbookRow[]>();
  for (const r of rows) {
    if (r.transaction_type !== 'sale_payment') continue;
    const list = salesByDate.get(r.transaction_date) ?? [];
    list.push(r);
    salesByDate.set(r.transaction_date, list);
  }

  const lastSaleId = new Map<string, string>();
  salesByDate.forEach((list, date) => {
    const last = list[list.length - 1];
    if (last) lastSaleId.set(date, last.id);
  });

  const out: DisplayRow[] = [];
  for (const r of rows) {
    const group = r.transaction_type === 'sale_payment' ? salesByDate.get(r.transaction_date) : undefined;
    if (!group || group.length < 2) {
      out.push({ kind: 'single', key: r.id, row: r });
      continue;
    }
    if (lastSaleId.get(r.transaction_date) !== r.id) continue; // folded into the group
    out.push({
      kind: 'sale_group',
      key: `sales-${r.transaction_date}`,
      date: r.transaction_date,
      rows: group,
      income: round2(group.reduce((s, g) => s + g.income_amount, 0)),
      running_balance: r.running_balance,
    });
  }
  return out;
}

/** Client-side sums (used for export sanity checks and tests). */
export function sumRows(rows: Pick<CashbookRow, 'income_amount' | 'expense_amount'>[]): { income: number; expense: number } {
  return rows.reduce(
    (acc, r) => ({
      income: round2(acc.income + Number(r.income_amount || 0)),
      expense: round2(acc.expense + Number(r.expense_amount || 0)),
    }),
    { income: 0, expense: 0 },
  );
}

// ── Period presets ────────────────────────────────────────────

export type PeriodPreset = 'today' | 'week' | 'month' | 'last_month' | 'fy' | 'all' | 'custom';

export const PERIOD_LABELS: Record<PeriodPreset, string> = {
  today: 'Today',
  week: 'This week',
  month: 'This month',
  last_month: 'Last month',
  fy: 'This FY',
  all: 'All time',
  custom: 'Custom',
};

export function toISODate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Returns an inclusive {from,to} for a preset. `all` → nulls. FY = Indian FY (Apr–Mar). */
export function getPresetRange(preset: PeriodPreset, today: Date = new Date()): { from: string | null; to: string | null } {
  const t = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  switch (preset) {
    case 'today':
      return { from: toISODate(t), to: toISODate(t) };
    case 'week': {
      const dow = (t.getDay() + 6) % 7; // Monday = 0
      const start = new Date(t); start.setDate(t.getDate() - dow);
      return { from: toISODate(start), to: toISODate(t) };
    }
    case 'month':
      return { from: toISODate(new Date(t.getFullYear(), t.getMonth(), 1)), to: toISODate(t) };
    case 'last_month': {
      const start = new Date(t.getFullYear(), t.getMonth() - 1, 1);
      const end = new Date(t.getFullYear(), t.getMonth(), 0);
      return { from: toISODate(start), to: toISODate(end) };
    }
    case 'fy': {
      const fyStartYear = t.getMonth() >= 3 ? t.getFullYear() : t.getFullYear() - 1;
      return { from: toISODate(new Date(fyStartYear, 3, 1)), to: toISODate(t) };
    }
    case 'all':
    case 'custom':
    default:
      return { from: null, to: null };
  }
}

// ── Errors ───────────────────────────────────────────────────

export interface AccountsError {
  code: string | null;
  message: string;
}

/** Parses "ACC_CODE: message" raised by the Accounts RPCs. */
export function parseRpcError(err: unknown): AccountsError {
  const raw =
    typeof err === 'string'
      ? err
      : err && typeof err === 'object' && 'message' in err
        ? String((err as { message: unknown }).message)
        : 'Something went wrong';
  const m = raw.match(/(ACC_[A-Z_]+):\s*([\s\S]*)$/);
  if (!m) return { code: null, message: raw };
  const code = m[1] ?? null;
  const rawMsg = m[2] ? m[2].trim() : '';
  const message = code === 'ACC_FORBIDDEN' ? "You don't have permission to do this" : rawMsg;
  return { code, message };
}

// ── Formatting ───────────────────────────────────────────────

export function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

const INR = new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2, minimumFractionDigits: 0 });

export function formatINR(n: number | null | undefined): string {
  return INR.format(Number(n ?? 0));
}

/** dd/mm/yyyy for display (BR-008). */
export function formatDisplayDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const [y, m, d] = iso.slice(0, 10).split('-');
  return d && m && y ? `${d}/${m}/${y}` : iso;
}
